<script setup lang="ts">
import { computed, ref, useTemplateRef } from "vue";
import { z } from "zod";
import {
    UiAside,
    UiButton,
    UiFormBuilder,
    UiIcon,
    UiText,
    useToaster,
    type BuilderRowConfig,
} from "@shopexperts/shared";

type RequestEmailChangeHandler = (newEmail: string, password: string) => Promise<unknown>;

const props = withDefaults(defineProps<{
    modelValue: boolean;
    requestEmailChange: RequestEmailChangeHandler;
    title?: string;
    description?: string;
    successTitle?: string;
    errorFallback?: string;
    submitLabel?: string;
}>(), {
    title: "Change Email",
    description: "A verification link will be sent to your new email address. Click the link to confirm the change.",
    successTitle: "Verification email sent! Please check your inbox.",
    errorFallback: "Failed to request email change",
    submitLabel: "Send Verification",
});

const emit = defineEmits<{
    "update:modelValue": [boolean];
}>();

const emailChangeSchema = z.object({
    new_email: z.email("Please enter a valid email address"),
    password: z.string().min(1, "Password is required"),
});

type EmailChangeFormValues = z.infer<typeof emailChangeSchema>;

const toaster = useToaster();
const formRef = useTemplateRef("formRef");
const model = ref<EmailChangeFormValues>();
const isDirty = ref(false);
const isValid = ref(false);
const isSaving = ref(false);

const isOpen = computed({
    get: () => props.modelValue,
    set: (value: boolean) => emit("update:modelValue", value),
});

const initialValues: EmailChangeFormValues = {
    new_email: "",
    password: "",
};

const rows = computed<BuilderRowConfig[]>(() => [
    {
        columns: [
            {
                name: "new_email",
                label: "New Email Address",
                type: "email",
                props: { placeholder: "Enter your new email" },
            },
        ],
    },
    {
        columns: [
            {
                name: "password",
                label: "Current Password",
                type: "password",
                hint: "Required for security verification",
                props: {
                    type: "password",
                    placeholder: "Enter your current password to confirm",
                },
            },
        ],
    },
]);

const canSubmit = computed(() => {
    return isDirty.value && isValid.value && model.value?.new_email && model.value?.password;
});

function closeAside() {
    isOpen.value = false;
}

function getErrorMessage(err: unknown, fallback: string): string {
    const error = err as { response?: { data?: { message?: string } }; message?: string };
    return error?.response?.data?.message || error?.message || fallback;
}

async function handleSubmit() {
    if (!model.value?.new_email || !model.value?.password) {
        return;
    }

    isSaving.value = true;
    try {
        await props.requestEmailChange(model.value.new_email, model.value.password);
        toaster.create({
            title: props.successTitle,
            type: "success",
        });
        formRef.value?.resetForm();
        closeAside();
    } catch (err: unknown) {
        toaster.create({
            title: getErrorMessage(err, props.errorFallback),
            type: "error",
        });
    } finally {
        isSaving.value = false;
    }
}
</script>

<template>
    <UiAside v-model="isOpen" position="right" :close-on-overlay-click="false">
        <div class="p-6 md:p-8 flex flex-col gap-6 max-h-full overflow-y-auto">
            <div class="flex items-center justify-between">
                <UiText as="h3" variant="h3">
                    {{ title }}
                </UiText>
                <UiButton variant="ghost" shape="square" @click="closeAside">
                    <template #before_label>
                        <UiIcon name="close" class="w-6 h-6" />
                    </template>
                </UiButton>
            </div>

            <UiText variant="p" as="p" class="text-gray-600">
                {{ description }}
            </UiText>

            <UiFormBuilder
                ref="formRef"
                :schema="emailChangeSchema"
                :rows="rows"
                :initial-values="initialValues"
                v-model="model"
                v-model:dirty="isDirty"
                v-model:valid="isValid"
            >
                <template #actions>
                    <div class="flex gap-3 mt-6">
                        <UiButton variant="outline" label="Cancel" class="flex-1" @click="closeAside" />
                        <UiButton
                            variant="primary"
                            :label="submitLabel"
                            class="flex-1"
                            :loading="isSaving"
                            :disabled="!canSubmit"
                            @click="handleSubmit"
                        />
                    </div>
                </template>
            </UiFormBuilder>
        </div>
    </UiAside>
</template>
